import { create } from 'zustand';
import { authService } from '../services/apiService';

export const useAuthStore = create((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,   // true until initAuth resolves — prevents flash to /login on refresh
  error: null,

  // Initialize auth from localStorage and restore user profile
  initAuth: async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      set({ isAuthenticated: false, isLoading: false });
      return;
    }
    try {
      const user = await authService.getProfile();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  // Login
  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login(email, password);
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false,
      });
      return response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Login failed';
      set({ error: errorMsg, isLoading: false });
      throw error;
    }
  },

  // Register
  register: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.register(data);
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false,
      });
      return response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Registration failed';
      set({ error: errorMsg, isLoading: false });
      throw error;
    }
  },

  // Get profile
  getProfile: async () => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.getProfile();
      set({ user, isLoading: false });
      return user;
    } catch (error) {
      set({ error: 'Failed to fetch profile', isLoading: false });
      throw error;
    }
  },

  // Update profile
  updateProfile: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.updateProfile(data);
      set({ user, isLoading: false });
      return user;
    } catch (error) {
      set({ error: 'Failed to update profile', isLoading: false });
      throw error;
    }
  },

  // Logout
  logout: () => {
    authService.logout();
    set({ user: null, isAuthenticated: false });
  },
}));

export const useContentStore = create((set) => ({
  generatedContent: null,
  contentHistory: [],
  isLoading: false,
  error: null,

  setGeneratedContent: (content) => set({ generatedContent: content }),
  setContentHistory: (history) => set({ contentHistory: history }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}));

export const useUIStore = create((set) => ({
  sidebarOpen: true,
  darkMode: false,
  toastMessage: null,

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setDarkMode: (isDark) => set({ darkMode: isDark }),
  showToast: (message) => set({ toastMessage: message }),
  hideToast: () => set({ toastMessage: null }),
}));

// Global input text — shared across content-generation pages/tabs so the
// user's pasted source text follows them as they navigate between pages.
// Output remains page-local (see GenPage / NewsAnchorFormats / etc.).
export const useInputStore = create((set) => ({
  globalInputText: '',

  setGlobalInputText: (text) => set({ globalInputText: text }),
  clearGlobalInputText: () => set({ globalInputText: '' }),
}));
